import { CheckOutlined, LoadingOutlined, ShopOutlined } from "@ant-design/icons";
import { Dropdown, Typography } from "antd";
import { showError } from "common/ToastNotifications";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { useHistory, withRouter } from "react-router";
import actions from "store/actions";
import CypressTestIds from "../cypress/CypressTestIds";

const { Text } = Typography;

function BusinessAreaSelectMenu() {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const [fetchingBusinessAreas, setFetchingBusinessAreas] = React.useState<boolean>(true);

    const businessAreas = useAppSelector((state) => state.home.businessAreas);
    const activeBusinessAreaId = useAppSelector((state) => state.home.activeBusinessAreaId);
    const getUserBusinessAreasResult = useAppSelector((state) => state.home.getUserBusinessAreasResult);
    const activeBusinessAreaPath = useAppSelector((state) => state.home.activeBusinessAreaPath);

    const getUserBusinessAreas = React.useCallback(() => dispatch(actions.getUserBusinessAreasRequest()), [dispatch]);
    const getUserActiveBusinessArea = React.useCallback(() => dispatch(actions.getUserActiveBusinessAreaRequest()), [dispatch]);
    const saveUserActiveBusinessArea = React.useCallback((businessAreaId: string) => dispatch(actions.saveUserActiveBusinessAreaRequest(businessAreaId)), [dispatch]);

    React.useEffect(() => {
        // On load
        getUserBusinessAreas();
        getUserActiveBusinessArea();
    }, [getUserActiveBusinessArea, getUserBusinessAreas]);

    React.useEffect(() => {
        if (getUserBusinessAreasResult) {
            if (!getUserBusinessAreasResult.success) {
                if (getUserBusinessAreasResult.code === "PERMISSION_DENIED") {
                    showError("Business Areas permission denied.");
                } else {
                    showError("Unable to get Business Areas at this moment.");
                }
            }
            setFetchingBusinessAreas(false);
        }
    }, [getUserBusinessAreasResult]);

    const onBusinessAreaSelectionChange = React.useCallback(
        (businessAreaId: string, businessAreaName: string) => {
            saveUserActiveBusinessArea(businessAreaId);
            if (activeBusinessAreaPath) {
                history.push(activeBusinessAreaPath.replace(":businessarea", businessAreaName.toLowerCase()));
            }
        },
        [activeBusinessAreaPath, history, saveUserActiveBusinessArea],
    );

    const getActiveBusinessAreaTitle = React.useCallback(() => {
        if (activeBusinessAreaId && businessAreas && businessAreas.length > 0) {
            let activeBusinessArea = businessAreas.find((item) => item.businessAreaId === activeBusinessAreaId);
            if (activeBusinessArea) {
                return (
                    <Text type="success" strong className="whitespace-nowrap">
                        <ShopOutlined className="mr-2" />
                        {activeBusinessArea.name}
                    </Text>
                );
            }
        }
        return (
            <div className="flex gap-2 whitespace-nowrap">
                <LoadingOutlined />
                <Text type="success" strong>
                    Loading
                </Text>
            </div>
        );
    }, [activeBusinessAreaId, businessAreas]);

    return (
        <Dropdown
            placement="bottomRight"
            disabled={fetchingBusinessAreas}
            menu={{
                items: [
                    {
                        key: "business-areas-group",
                        type: "group",
                        label: "Business Area",
                        children: [...businessAreas]
                            .sort((a, b) => {
                                // Always put DEFAULT first
                                if (a.name.toUpperCase() === "DEFAULT") {
                                    return -1;
                                }
                                if (b.name.toUpperCase() === "DEFAULT") {
                                    return 1;
                                }
                                // Sort rest alphabetically
                                return a.name.localeCompare(b.name);
                            })
                            .map((businessArea) => ({
                                key: businessArea.businessAreaId,
                                icon: <CheckOutlined className={activeBusinessAreaId === businessArea.businessAreaId ? "text-green-600" : "opacity-0"} />,
                                onClick: () => onBusinessAreaSelectionChange(businessArea.businessAreaId, businessArea.name),
                                label: businessArea.name,
                            })),
                    },
                ],
            }}
        >
            <div className="header-menu-item whitespace-nowrap" data-testid={CypressTestIds.HEADER_MENU_BUSINESS_AREA_SELECT_MENU_BUTTON}>
                {getActiveBusinessAreaTitle()}
            </div>
        </Dropdown>
    );
}

export default withRouter(BusinessAreaSelectMenu);
